import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    CardContent
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import UsersTable from '../../../users/UsersTable';
import GroupTable from '../../../on-demand/Tables/GroupTable';
import AdditionalDataTable from '../../../on-demand/Tables/AdditionalDataTable';
import MainCard from '../../../../components/cards/MainCard';
import { MODAL_BORDER_RADIUS } from '../../utils/dateUtils';

const RecipientSelectionModal = ({
    open,
    onClose,
    onConfirm,
    initialRecipientType = 'Tenancy',
    initialRecipientId = null,
    initialRecipientName = null
}) => {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';

    const [recipientType, setRecipientType] = useState(initialRecipientType);
    const [recipientId, setRecipientId] = useState(initialRecipientId ? [initialRecipientId] : []);
    const [recipientName, setRecipientName] = useState(initialRecipientName || '');

    const user = JSON.parse(localStorage.getItem('user')) || {};
    const isUser = user.role === '';

    // Reset state when modal opens with new initial values
    useEffect(() => {
        if (open) {
            setRecipientType(initialRecipientType);
            setRecipientId(initialRecipientId ? [initialRecipientId] : []);
            setRecipientName(initialRecipientName || '');
        }
    }, [open, initialRecipientType, initialRecipientId, initialRecipientName]);

    // Force "User" for non-admin users
    useEffect(() => {
        if (isUser && recipientType !== 'User') {
            setRecipientType('User');
        }
    }, [isUser, recipientType]);

    const handleRecipientTypeChange = (event, newType) => {
        if (newType === null) return;
        setRecipientType(newType);
        setRecipientId([]);
        setRecipientName('');
    };

    // Handle selection from tables - extract the relevant ID and name
    const handleSelectionChange = (selected) => {
        setRecipientId(selected);

        // Extract name based on recipient type
        if (selected && selected.length > 0) {
            const item = selected[0];
            switch (recipientType) {
                case 'User':
                    setRecipientName(item.email || item.id || '');
                    break;
                case 'Group':
                    setRecipientName(item.groupName || item.name || '');
                    break;
                case 'Department':
                case 'Location':
                case 'City':
                case 'Country':
                case 'Company':
                case 'State':
                    setRecipientName(item.name || item.value || '');
                    break;
                default:
                    setRecipientName('');
            }
        } else {
            setRecipientName('');
        }
    };

    const handleConfirm = () => {
        // Build the recipient data object
        let sendToId = '';
        let displayName = '';

        if (recipientType === 'Tenancy') {
            // Tenancy doesn't need a specific ID from selection
            sendToId = ''; // Will be set to tenancyId in the hook
            displayName = 'All Users';
        } else if (recipientType === 'User' && recipientId.length > 0) {
            sendToId = recipientId[0].email;
            displayName = recipientId[0].email;
        } else if (recipientId.length > 0) {
            // Group types
            const item = recipientId[0];
            sendToId = (item.userGroupId || item.id || '').toString();
            displayName = recipientName;
        }

        onConfirm({
            recipientType,
            sendToId,
            displayName,
            rawSelection: recipientId[0] || null
        });
    };

    const isConfirmDisabled = () => {
        if (recipientType === 'Tenancy') return false;
        return recipientId.length === 0;
    };

    const getRecipientTypeLabel = (type) => {
        const labels = {
            'Tenancy': 'All Users',
            'User': 'User',
            'Group': 'Group',
            'Department': 'Department',
            'Location': 'Location',
            'City': 'City',
            'Country': 'Country',
            'Company': 'Company',
            'State': 'State'
        };
        return labels[type] || type;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '70vh',
                    maxHeight: '90vh',
                    borderRadius: MODAL_BORDER_RADIUS
                }
            }}
        >
            <DialogTitle sx={{ bgcolor: 'background.default' }}>
                Select Journey Recipients
            </DialogTitle>
            <DialogContent sx={{ bgcolor: 'background.default' }}>
                <Box sx={{ width: '100%' }}>
                    {!isUser && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, width: '100%' }}>
                            <ToggleButtonGroup
                                exclusive
                                value={recipientType}
                                onChange={handleRecipientTypeChange}
                                aria-label="recipient type"
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    width: '100%',
                                    '& .MuiToggleButton-root': {
                                        flex: 1,
                                        minWidth: '100px',
                                        px: 2
                                    }
                                }}
                            >
                                <ToggleButton value="Tenancy">All Users</ToggleButton>
                                <ToggleButton value="User">User</ToggleButton>
                                <ToggleButton value="Group">Group</ToggleButton>
                                <ToggleButton value="Department">Department</ToggleButton>
                                <ToggleButton value="Location">Location</ToggleButton>
                                <ToggleButton value="City">City</ToggleButton>
                                <ToggleButton value="Country">Country</ToggleButton>
                                <ToggleButton value="Company">Company</ToggleButton>
                                <ToggleButton value="State">State</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    <Box sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    // Constrain table height to fit within modal without causing modal scrollbar
                    // Accounts for: DialogTitle (~64px), ToggleButtons (~80px), DialogActions (~72px), padding (~64px)
                    maxHeight: 'calc(90vh - 280px)',
                    minHeight: 0,
                    overflow: 'auto',
                    // Ensure scrollbar appears on this container, not on the modal
                    '& .MuiTableContainer-root': {
                        maxHeight: 'calc(90vh - 400px) !important'
                    }
                }}>
                        {recipientType === 'Tenancy' && (
                            <MainCard sx={{
                                width: '50%',
                                textAlign: 'center',
                                mt: 3,
                                borderRadius: 2,
                                bgcolor: lightMode ? theme.palette.white : theme.palette.grey[700],
                            }}>
                                <CardContent>
                                    <Typography variant="h5">
                                        Content will be sent to all users in your tenancy.
                                    </Typography>
                                </CardContent>
                            </MainCard>
                        )}

                        {recipientType === 'User' && (
                            <UsersTable
                                type="selectOne"
                                userIds={recipientId}
                                setUserId={handleSelectionChange}
                                CSVButton={null}
                            />
                        )}

                        {recipientType === 'Group' && (
                            <GroupTable
                                type="select"
                                groupIds={recipientId}
                                setGroupId={handleSelectionChange}
                                singleSelect={true}
                                hideFilterSwitch={true}
                            />
                        )}

                        {['Department', 'Location', 'City', 'Country', 'Company', 'State'].includes(recipientType) && (
                            <AdditionalDataTable
                                name={recipientType === 'Company' ? 'CompanyName' : recipientType}
                                type="select"
                                selectedIds={recipientId}
                                setSelectedIds={handleSelectionChange}
                                singleSelect={true}
                            />
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: 'background.default', p: 2, gap: 1 }}>
                <Button variant="contained" color="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                    disabled={isConfirmDisabled()}
                >
                    Continue to Calendar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

RecipientSelectionModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    initialRecipientType: PropTypes.string,
    initialRecipientId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    initialRecipientName: PropTypes.string
};

export default RecipientSelectionModal;